export const content = {
  en: {
    nav: {
      features: 'Features',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      calculator: 'Savings',
      testimonials: 'Testimonials',
      faq: 'FAQ',
      getStarted: 'Get Started Free',
      login: 'Login',
    },
    hero: {
      badge: 'The #1 Commission-Free Restaurant Platform',
      title: 'Keep More',
      titleHighlight: 'From Every Order',
      subtitle: 'NoFee.restaurant gives your restaurant a digital menu, direct payments via Stripe, and orders through SMS, WhatsApp & Email — so you keep 100% of your sales.',
      cta: 'Start Free Trial',
      secondaryCta: 'See How It Works',
      stats: [
        { value: '$0', label: 'Commission Fees' },
        { value: '2,500+', label: 'Restaurants' },
        { value: '$12M+', label: 'Saved in Fees' },
      ],
    },
    problem: {
      badge: 'The Problem',
      title: 'Marketplaces Are',
      titleHighlight: 'Eating Your Profits',
      subtitle: 'Per-order commissions on food-delivery marketplaces add up fast — meaningful margin disappears every month before any extra promo, payout, or visibility fees.',
      cards: [
        { platform: 'NoFee.restaurant', fee: '0%', monthly: '$299/mo flat', color: '#D4A017', highlight: true },
      ],
    },
    howItWorks: {
      badge: 'How It Works',
      title: 'Four Simple Steps to',
      titleHighlight: 'Keep Your Revenue',
      subtitle: 'Get set up in under 24 hours and start receiving orders directly.',
      steps: [
        {
          step: '01',
          title: 'Create Your Digital Menu',
          description: 'Upload your menu in minutes. Our AI helps optimize descriptions, pricing, and photos to maximize orders.',
        },
        {
          step: '02',
          title: 'Share With Customers',
          description: 'Customers access your menu via QR code, link, SMS, or WhatsApp. No app downloads needed.',
        },
        {
          step: '03',
          title: 'Receive Direct Payments',
          description: 'Customers pay directly via Stripe. Money goes straight to your bank account — no middleman.',
        },
        {
          step: '04',
          title: 'Deliver & Grow',
          description: 'Use your own delivery team. Track orders, build customer relationships, and grow with AI-powered marketing.',
        },
      ],
    },
    features: {
      badge: 'Powerful Features',
      title: 'Everything You Need,',
      titleHighlight: 'Nothing You Don\'t',
      subtitle: 'A complete platform to run your restaurant\'s digital ordering, payments, and marketing.',
      items: [
        {
          title: 'AI-Powered Digital Menu',
          description: 'Beautiful, mobile-optimized menus with AI-generated descriptions and smart pricing suggestions.',
          icon: 'UtensilsCrossed',
        },
        {
          title: 'Direct Stripe Payments',
          description: 'Customers pay you directly. Payments go directly to your own Stripe account. NoFee never holds your money.',
          icon: 'CreditCard',
        },
        {
          title: 'SMS & WhatsApp Orders',
          description: 'Receive orders via SMS and WhatsApp. Perfect for reaching all communities.',
          icon: 'MessageSquare',
        },
        {
          title: 'AI Chatbot Assistant',
          description: 'Automated order-taking chatbot that handles customers 24/7 in English and Spanish.',
          icon: 'Bot',
        },
        {
          title: 'Marketing Automation',
          description: 'Send targeted promotions via SMS, email, and social media to bring customers back.',
          icon: 'Megaphone',
        },
        {
          title: 'Real-Time Analytics',
          description: 'Track sales, popular items, peak hours, and customer behavior with smart dashboards.',
          icon: 'BarChart3',
        },
        {
          title: 'Loyalty Program',
          description: 'Built-in points system that rewards repeat customers and increases order frequency.',
          icon: 'Heart',
        },
        {
          title: 'Multi-Language Support',
          description: 'Menus and ordering in English, Spanish, Portuguese, and more. Reach every customer.',
          icon: 'Globe',
        },
        {
          title: 'Order Management',
          description: 'Complete POS system with kitchen display, order tracking, and delivery management.',
          icon: 'ClipboardList',
        },
      ],
    },
    calculator: {
      badge: 'Savings Calculator',
      title: 'See How Much You\'re',
      titleHighlight: 'Losing to Commissions',
      subtitle: 'Enter your monthly delivery revenue to see your potential savings with NoFee.restaurant.',
      revenueLabel: 'Monthly Delivery Revenue',
      commissionLabel: 'Current Commission Rate',
      resultTitle: 'Your Annual Savings',
      currentCost: 'Current Commission Cost',
      withNoFee: 'With NoFee.restaurant',
      youSave: 'You Save Per Year',
      perMonth: '/month',
      perYear: '/year',
    },
    pricing: {
      badge: 'Simple Pricing',
      title: 'One Plan,',
      titleHighlight: 'Everything Included',
      subtitle: 'Flat monthly pricing. Cancel anytime. Lock the Early Bird rate forever.',
      plans: [
        {
          name: '',
          price: '$299',
          originalPrice: '$399',
          period: '/month',
          badge: 'EARLY BIRD — LIFETIME LOCK',
          description: 'Get the Early Bird price for the next 6 months and keep it forever. New restaurants will pay $399/mo after that. Everything you need to run direct orders — no commissions, no surprises.',
          features: [
            'Unlimited digital menu items',
            'QR code ordering at every table',
            'WhatsApp + SMS + Email order notifications',
            'Stripe payments (0% NoFee commission)',
            'Loyalty & referral program included',
            'Marketing automation (email + SMS)',
            'Advanced analytics & reports',
            'Priority human support',
          ],
          cta: 'Lock my Early Bird price',
          popular: true,
        },
      ],
    },
    testimonials: {
      badge: 'Success Stories',
      title: 'Restaurants That',
      titleHighlight: 'Switched & Saved',
      subtitle: 'Hear from real restaurant owners who stopped paying commissions.',
      items: [
        {
          name: 'Carlos Mendez',
          role: 'Owner, Sabor Latino Kitchen',
          location: 'Miami, FL',
          quote: 'We were paying DoorDash over $8,000 a month in commissions. With NoFee.restaurant, we keep all of that. The WhatsApp ordering is perfect for our community.',
          savings: '$96,000/year saved',
          image: 'https://images.unsplash.com/photo-1512485800893-b08ec1ea59b1?w=150&h=150&fit=crop&crop=face',
        },
        {
          name: 'Maria Santos',
          role: 'Owner, Casa da Maria',
          location: 'Newark, NJ',
          quote: 'My customers love ordering through WhatsApp. It feels personal, and the money goes straight to my account. No more waiting weeks for DoorDash to pay me.',
          savings: '$72,000/year saved',
          image: 'https://images.unsplash.com/photo-1643834776503-891726ed42c6?w=150&h=150&fit=crop&crop=face',
        },
        {
          name: 'Jin Park',
          role: 'Owner, Seoul Kitchen',
          location: 'Los Angeles, CA',
          quote: 'The AI chatbot handles orders in English and Spanish while I focus on cooking. Best investment I\'ve made for my restaurant.',
          savings: '$108,000/year saved',
          image: 'https://images.unsplash.com/photo-1532635211-8ec15f2ce05c?w=150&h=150&fit=crop&crop=face',
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Frequently Asked',
      titleHighlight: 'Questions',
      items: [
        {
          q: 'How does NoFee.restaurant work?',
          a: 'NoFee.restaurant provides your restaurant with a custom digital menu that customers can access via QR code, link, SMS, or WhatsApp. Orders come directly to you, and payments go straight to your bank account via Stripe. No middleman, no commissions.',
        },
        {
          q: 'Is there really no commission?',
          a: 'Correct! We charge a flat monthly fee. You pay zero commission on orders. Stripe charges their standard processing fee (2.9% + 30¢), which is the same for any online payment — but there are no NoFee.restaurant commissions on top of that.',
        },
        {
          q: 'Do my customers need to download an app?',
          a: 'No! Your digital menu works in any web browser. Customers simply scan a QR code or click a link to view your menu and place an order. No downloads, no account creation required.',
        },
        {
          q: 'How quickly can I get set up?',
          a: 'Most restaurants are fully set up within 24 hours. Upload your menu, connect your Stripe account, and you\'re ready to receive orders. Our team helps you every step of the way.',
        },
        {
          q: 'Can I still use DoorDash/GrubHub alongside NoFee.restaurant?',
          a: 'Absolutely! Many restaurants use NoFee.restaurant for direct orders while keeping their presence on delivery apps. Over time, most owners shift more volume to direct orders to save on commissions.',
        },
        {
          q: 'How do deliveries work?',
          a: 'You use your own delivery team. Most restaurants already have delivery drivers. NoFee.restaurant provides route optimization and delivery tracking tools to make it efficient.',
        },
        {
          q: 'Does it work with WhatsApp?',
          a: 'Yes! WhatsApp ordering is one of our most popular features, especially with Latino, Asian, and immigrant communities. Customers can browse your menu and place orders directly through WhatsApp.',
        },
        {
          q: 'What payment methods are supported?',
          a: 'Through Stripe, we support all major credit and debit cards, Apple Pay, Google Pay, and more. Payments are sent directly to your Stripe account according to your Stripe payout schedule. NoFee never holds your money.',
        },
      ],
    },
    cta: {
      title: 'Ready to Keep',
      titleHighlight: '100% of Your Sales?',
      subtitle: 'Join 2,500+ restaurants that stopped paying commissions. Start your free 14-day trial today.',
      button: 'Start Free Trial',
      secondaryButton: 'Schedule a Demo',
    },
    footer: {
      description: 'The commission-free restaurant platform. Digital menus, direct payments, and AI-powered ordering for modern restaurants.',
      product: 'Product',
      company: 'Company',
      support: 'Support',
      legal: 'Legal',
      links: {
        product: [
          { label: 'Features', href: '#features' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'Rewards', href: '/rewards' },
          { label: 'How it works', href: '#how-it-works' },
        ],
        company: [
          { label: 'About Us', href: '/about' },
          { label: 'Careers', href: '#' },
          { label: 'Blog', href: '#' },
          { label: 'Press', href: '#' },
        ],
        support: [
          { label: 'Help Center', href: '/contact' },
          { label: 'Contact Us', href: '/contact' },
        ],
        legal: [
          { label: 'Terms of Service', href: '/terms' },
          { label: 'Privacy Policy', href: '/privacy' },
          { label: 'Cookie Policy', href: '/cookies' },
          { label: 'Rewards Terms', href: '/rewards-terms' },
          { label: 'Refund Policy', href: '/refund' },
        ],
      },
      copyright: '© 2026 NoFee LLC. All rights reserved.',
    },
  },
  es: {
    nav: {
      features: 'Funciones',
      howItWorks: 'Cómo Funciona',
      pricing: 'Precios',
      calculator: 'Ahorro',
      testimonials: 'Testimonios',
      faq: 'FAQ',
      getStarted: 'Comenzar Gratis',
      login: 'Iniciar Sesión',
    },
    hero: {
      badge: 'La Plataforma #1 Sin Comisiones para Restaurantes',
      title: 'Quédate Con Más',
      titleHighlight: 'De Cada Pedido',
      subtitle: 'NoFee.restaurant le da a tu restaurante un menú digital, pagos directos vía Stripe y pedidos por SMS, WhatsApp y Email — para que te quedes con el 100% de tus ventas.',
      cta: 'Prueba Gratis',
      secondaryCta: 'Ver Cómo Funciona',
      stats: [
        { value: '$0', label: 'Comisiones' },
        { value: '2,500+', label: 'Restaurantes' },
        { value: '$12M+', label: 'Ahorrados en Comisiones' },
      ],
    },
    problem: {
      badge: 'El Problema',
      title: 'Las Plataformas Están',
      titleHighlight: 'Comiéndose Tus Ganancias',
      subtitle: 'Las comisiones por pedido en plataformas de delivery se acumulan rápido — un margen importante desaparece cada mes antes incluso de tarifas extra de promoción, payout o visibilidad.',
      cards: [
        { platform: 'NoFee.restaurant', fee: '0%', monthly: '$299/mes fijo', color: '#D4A017', highlight: true },
      ],
    },
    howItWorks: {
      badge: 'Cómo Funciona',
      title: 'Cuatro Pasos Simples para',
      titleHighlight: 'Mantener Tus Ingresos',
      subtitle: 'Configúrate en menos de 24 horas y comienza a recibir pedidos directamente.',
      steps: [
        {
          step: '01',
          title: 'Crea Tu Menú Digital',
          description: 'Sube tu menú en minutos. Nuestra IA ayuda a optimizar descripciones, precios y fotos.',
        },
        {
          step: '02',
          title: 'Comparte con Clientes',
          description: 'Los clientes acceden a tu menú por código QR, enlace, SMS o WhatsApp. Sin descargar apps.',
        },
        {
          step: '03',
          title: 'Recibe Pagos Directos',
          description: 'Los clientes pagan directo por Stripe. El dinero va a tu cuenta bancaria — sin intermediarios.',
        },
        {
          step: '04',
          title: 'Entrega y Crece',
          description: 'Usa tu propio equipo de entrega. Rastrea pedidos, construye relaciones y crece con marketing de IA.',
        },
      ],
    },
    features: {
      badge: 'Funciones Poderosas',
      title: 'Todo Lo Que Necesitas,',
      titleHighlight: 'Nada Que No',
      subtitle: 'Una plataforma completa para pedidos digitales, pagos y marketing de tu restaurante.',
      items: [
        {
          title: 'Menú Digital con IA',
          description: 'Menús hermosos y optimizados para móvil con descripciones generadas por IA.',
          icon: 'UtensilsCrossed',
        },
        {
          title: 'Pagos Directos con Stripe',
          description: 'Los clientes te pagan directamente. El dinero llega en 1-2 días. Sin comisión, nunca.',
          icon: 'CreditCard',
        },
        {
          title: 'Pedidos por SMS y WhatsApp',
          description: 'Recibe pedidos por SMS y WhatsApp. Perfecto para todas las comunidades.',
          icon: 'MessageSquare',
        },
        {
          title: 'Chatbot con IA',
          description: 'Chatbot automatizado que atiende clientes 24/7 en inglés y español.',
          icon: 'Bot',
        },
        {
          title: 'Marketing Automatizado',
          description: 'Envía promociones por SMS, email y redes sociales para atraer clientes.',
          icon: 'Megaphone',
        },
        {
          title: 'Análisis en Tiempo Real',
          description: 'Rastrea ventas, items populares, horas pico y comportamiento de clientes.',
          icon: 'BarChart3',
        },
        {
          title: 'Programa de Fidelidad',
          description: 'Sistema de puntos que premia clientes frecuentes y aumenta pedidos.',
          icon: 'Heart',
        },
        {
          title: 'Multi-Idioma',
          description: 'Menús y pedidos en inglés, español, portugués y más.',
          icon: 'Globe',
        },
        {
          title: 'Gestión de Pedidos',
          description: 'Sistema POS completo con pantalla de cocina, rastreo y gestión de entregas.',
          icon: 'ClipboardList',
        },
      ],
    },
    calculator: {
      badge: 'Calculadora de Ahorro',
      title: 'Mira Cuánto Estás',
      titleHighlight: 'Perdiendo en Comisiones',
      subtitle: 'Ingresa tus ingresos mensuales de delivery para ver tu ahorro potencial con NoFee.restaurant.',
      revenueLabel: 'Ingresos Mensuales de Delivery',
      commissionLabel: 'Tasa de Comisión Actual',
      resultTitle: 'Tu Ahorro Anual',
      currentCost: 'Costo Actual de Comisión',
      withNoFee: 'Con NoFee.restaurant',
      youSave: 'Ahorras Por Año',
      perMonth: '/mes',
      perYear: '/año',
    },
    pricing: {
      badge: 'Precios Simples',
      title: 'Un Plan,',
      titleHighlight: 'Todo Incluido',
      subtitle: 'Precio mensual fijo. Cancela cuando quieras. Asegura el precio Early Bird para siempre.',
      plans: [
        {
          name: '',
          price: '$299',
          originalPrice: '$399',
          period: '/mes',
          badge: 'EARLY BIRD — PRECIO DE POR VIDA',
          description: 'Obtén el precio Early Bird en los próximos 6 meses y consérvalo para siempre. Los nuevos restaurantes pagarán $399/mes después. Todo lo que necesitas para vender directo — sin comisiones, sin sorpresas.',
          features: [
            'Items de menú ilimitados',
            'Pedidos por código QR en cada mesa',
            'Notificaciones WhatsApp + SMS + Email',
            'Pagos con Stripe (0% comisión NoFee)',
            'Programa de fidelidad y referidos incluido',
            'Marketing automatizado (email + SMS)',
            'Análisis y reportes avanzados',
            'Soporte humano prioritario',
          ],
          cta: 'Asegurar mi precio Early Bird',
          popular: true,
        },
      ],
    },
    testimonials: {
      badge: 'Historias de Éxito',
      title: 'Restaurantes Que',
      titleHighlight: 'Cambiaron y Ahorraron',
      subtitle: 'Escucha a dueños de restaurantes reales que dejaron de pagar comisiones.',
      items: [
        {
          name: 'Carlos Mendez',
          role: 'Dueño, Sabor Latino Kitchen',
          location: 'Miami, FL',
          quote: 'Estábamos pagando a DoorDash más de $8,000 al mes en comisiones. Con NoFee.restaurant, nos quedamos con todo. Los pedidos por WhatsApp son perfectos para nuestra comunidad.',
          savings: '$96,000/año ahorrados',
          image: 'https://images.unsplash.com/photo-1512485800893-b08ec1ea59b1?w=150&h=150&fit=crop&crop=face',
        },
        {
          name: 'Maria Santos',
          role: 'Dueña, Casa da Maria',
          location: 'Newark, NJ',
          quote: 'A mis clientes les encanta pedir por WhatsApp. Se siente personal, y el dinero va directo a mi cuenta. No más esperar semanas por el pago de DoorDash.',
          savings: '$72,000/año ahorrados',
          image: 'https://images.unsplash.com/photo-1643834776503-891726ed42c6?w=150&h=150&fit=crop&crop=face',
        },
        {
          name: 'Jin Park',
          role: 'Dueño, Seoul Kitchen',
          location: 'Los Angeles, CA',
          quote: 'El chatbot con IA maneja pedidos en inglés y español mientras yo me concentro en cocinar. La mejor inversión para mi restaurante.',
          savings: '$108,000/año ahorrados',
          image: 'https://images.unsplash.com/photo-1532635211-8ec15f2ce05c?w=150&h=150&fit=crop&crop=face',
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Preguntas',
      titleHighlight: 'Frecuentes',
      items: [
        {
          q: '¿Cómo funciona NoFee.restaurant?',
          a: 'NoFee.restaurant proporciona a tu restaurante un menú digital personalizado al que los clientes acceden por código QR, enlace, SMS o WhatsApp. Los pedidos llegan directamente a ti y los pagos van a tu cuenta bancaria por Stripe.',
        },
        {
          q: '¿Realmente no hay comisión?',
          a: '¡Correcto! Cobramos una tarifa mensual fija. Pagas cero comisión por pedidos. Stripe cobra su tarifa estándar (2.9% + 30¢), pero no hay comisiones de NoFee.restaurant encima de eso.',
        },
        {
          q: '¿Mis clientes necesitan descargar una app?',
          a: '¡No! Tu menú digital funciona en cualquier navegador. Los clientes simplemente escanean un código QR o hacen clic en un enlace.',
        },
        {
          q: '¿Qué tan rápido puedo configurarlo?',
          a: 'La mayoría de restaurantes están totalmente configurados en 24 horas. Sube tu menú, conecta tu cuenta Stripe y estarás listo.',
        },
        {
          q: '¿Puedo seguir usando DoorDash/GrubHub junto con NoFee.restaurant?',
          a: '¡Por supuesto! Muchos restaurantes usan NoFee.restaurant para pedidos directos mientras mantienen presencia en apps de delivery.',
        },
        {
          q: '¿Cómo funcionan las entregas?',
          a: 'Usas tu propio equipo de entrega. NoFee.restaurant proporciona herramientas de optimización de rutas y rastreo de entregas.',
        },
        {
          q: '¿Funciona con WhatsApp?',
          a: '¡Sí! Los pedidos por WhatsApp son una de nuestras funciones más populares, especialmente con comunidades latinas y asiáticas.',
        },
        {
          q: '¿Qué métodos de pago se aceptan?',
          a: 'A través de Stripe, aceptamos todas las tarjetas de crédito y débito principales, Apple Pay, Google Pay y más.',
        },
      ],
    },
    cta: {
      title: '¿Listo para Quedarte con el',
      titleHighlight: '100% de Tus Ventas?',
      subtitle: 'Únete a 2,500+ restaurantes que dejaron de pagar comisiones. Comienza tu prueba gratis de 14 días hoy.',
      button: 'Prueba Gratis',
      secondaryButton: 'Agendar Demo',
    },
    footer: {
      description: 'La plataforma de restaurantes sin comisiones. Menús digitales, pagos directos y pedidos potenciados por IA.',
      product: 'Producto',
      company: 'Compañía',
      support: 'Soporte',
      legal: 'Legal',
      links: {
        product: [
          { label: 'Funciones', href: '#features' },
          { label: 'Precios', href: '#pricing' },
          { label: 'Rewards', href: '/rewards' },
          { label: 'Cómo funciona', href: '#how-it-works' },
        ],
        company: [
          { label: 'Sobre Nosotros', href: '#' },
          { label: 'Carreras', href: '#' },
          { label: 'Blog', href: '#' },
          { label: 'Prensa', href: '#' },
        ],
        support: [
          { label: 'Centro de Ayuda', href: '/contact' },
          { label: 'Contáctanos', href: '/contact' },
        ],
        legal: [
          { label: 'Términos del Servicio', href: '/terms' },
          { label: 'Política de Privacidad', href: '/privacy' },
          { label: 'Política de Cookies', href: '/cookies' },
          { label: 'Términos de Rewards', href: '/rewards-terms' },
          { label: 'Política de Reembolso', href: '/refund' },
        ],
      },
      copyright: '© 2026 NoFee LLC. Todos los derechos reservados.',
    },
  },
};
